import { FastFieldProps, FormikHandlers } from "formik";
import dynamic from "next/dynamic";
import React from "react";
import styled from "styled-components";
import { pxToRem } from "@input-output-hk/px-to-rem";
import { forTabletLandscapeUp } from "../../tokens/media-queries";
import zIndex from "../../tokens/z-index";
import { ErrorMessage } from "../input/Input";
import { Props as MarkdownEditorProps } from "../markdown-editor/MarkdownEditor";
import {
  RelatedContentSelector,
  useRelatedContentContext
} from "./CustomMarkdownComponents";
import { FormFieldElement, FormFieldInputWrapper } from "./FormFieldElement";
import { FormFieldLabel } from "./FormFieldLabel";
import LoadingSpinner from "../LoadingSpinner";

const MarkdownEditor = dynamic(
  () => import("../markdown-editor/MarkdownEditor"),
  {
    ssr: false,
    loading: () => <LoadingSpinner size={"huge"} />
  }
);

type Props = Pick<
  MarkdownEditorProps,
  "appearance" | "disabled" | "placeholder"
> & {
  label: string;
  name: string;
} & FastFieldProps &
  Pick<FormikHandlers, "getFieldHelpers">;

const StyledDiv = styled.div`
  position: relative;
  background: white;
  overflow: initial;

  .remirror-toolbar,
  .markdown-label {
    z-index: ${zIndex.base};
    background: white;
    position: sticky;
  }

  .remirror-floating-popover {
    z-index: ${zIndex.base + 1};
  }

  .remirror-editor-wrapper {
    position: relative;
    z-index: ${zIndex.base};
  }

  .markdown-label {
    padding-bottom: ${pxToRem(10)};
    top: ${pxToRem(60)};
    padding-top: ${pxToRem(10)};
    margin-bottom: 0;
  }

  .remirror-toolbar {
    top: ${pxToRem(100)};
    padding-bottom: ${pxToRem(10)};
    padding-top: ${pxToRem(0)};
  }

  @media ${forTabletLandscapeUp} {
    .markdown-label {
      top: ${pxToRem(85)};
    }

    .remirror-toolbar {
      top: ${pxToRem(125)};
    }
  }
`;

const MarkdownField: React.FC<Props> = ({
  name,
  label,
  meta,
  getFieldHelpers,
  ...props
}) => {
  const helpers = getFieldHelpers(name);
  const showError = meta.touched && !!meta.error;
  const { isSelected, related } = useRelatedContentContext(
    name as RelatedContentSelector
  );

  return (
    <FormFieldElement data-related-selected={isSelected}>
      <StyledDiv>
        <FormFieldLabel className="markdown-label">{label}</FormFieldLabel>
        <FormFieldInputWrapper>
          <MarkdownEditor
            initialContent={meta.initialValue}
            onChange={helpers.setValue}
            {...props}
          />
          {related}
        </FormFieldInputWrapper>
      </StyledDiv>

      {showError && <ErrorMessage>{meta.error}</ErrorMessage>}
    </FormFieldElement>
  );
};

export default MarkdownField;
